import { DeviceCountDTO, MonitoringService, ScopeDTO } from '@activia/cm-api';
import { AuthFacade } from '@amp/auth';
import { Injectable } from '@angular/core';
import { TimerService } from '@amp/messenger';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { ITimeSeriesDataSerie } from '../models/time-series-data-serie.interface';
import { catchError, map, shareReplay, switchMap, takeWhile } from 'rxjs/operators';
import { TranslocoService } from '@ngneat/transloco';

@Injectable()
export class MonitoringSpeedService {
  public static refreshDelay: number = 1000 * 10; // 10 sec

  private deviceGroupId = -1;

  private _subscriberCount = new BehaviorSubject(0);
  private _timer$: Observable<number>;

  get monitoringSpeed$(): Observable<ITimeSeriesDataSerie> {
    return this.monitoredDeviceCount$.pipe(
      map((deviceCount) => ({ x: new Date(Date.now()).getTime(), y: Number(deviceCount.count) })),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  constructor(private monitoringService: MonitoringService, private authFacade: AuthFacade, private timerService: TimerService, private translateService: TranslocoService) {
    this.authFacade.authenticatedUserScopes$.subscribe((scopes: Array<ScopeDTO>) => {
      if (scopes && scopes.length > 0) {
        const authorizeAllDeviceGroups = scopes.filter((scope) => scope.authorizeAllDeviceGroups === true).length > 0;
        if (authorizeAllDeviceGroups) {
          this.deviceGroupId = 1;
        }
      }
    });
  }

  start() {
    this._subscriberCount.next(this._subscriberCount.value + 1);
  }

  stop() {
    const subscribers = this._subscriberCount.value - 1;
    this._subscriberCount.next(subscribers);
    if (subscribers < 1) {
      this._timer$ = undefined;
    }
  }

  startTimer() {
    this._timer$ = this.timerService.timer$(MonitoringSpeedService.refreshDelay).pipe(
      takeWhile(() => this._subscriberCount.value > 0),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  get monitoredDeviceCount$(): Observable<DeviceCountDTO> {
    if (this._timer$ == null) {
      this.startTimer();
    }

    return this._timer$.pipe(
      switchMap(() => {
        if (this.deviceGroupId < 0) {
          this._subscriberCount.next(0);
          return throwError(this.translateService.translate('dashboardScope.dashboard.monitoring-speed.error.no-authorizeAllDeviceGroups-permission'));
        }
        return this.monitoringService.getCountLastAttemptDateRange(MonitoringSpeedService.refreshDelay / 1000).pipe(
          map((count) => {
            count.count = count.count * (60 / (MonitoringSpeedService.refreshDelay / 1000));
            return count;
          }),
          catchError(() => {
            this._subscriberCount.next(0);
            return throwError(this.translateService.translate('dashboardScope.dashboard.monitoring-speed.error.fail-to-load-data'));
          })
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}
